import React, { useState } from 'react';
import { MapPin, Navigation, Loader2 } from 'lucide-react';
import { getCurrentLocation } from '../utils/prayerTimes';
import { getTranslation } from '../utils/translations';
import { getIconColor, getButtonStyles } from '../utils/themeUtils';
import type { UserLocation, Language, Theme } from '../types';

interface LocationSettingsProps {
  isDark: boolean;
  onLocationUpdate: (location: UserLocation) => void;
  currentLocation?: UserLocation;
  language: Language;
  theme: Theme;
}

export function LocationSettings({ isDark, onLocationUpdate, currentLocation, language, theme }: LocationSettingsProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGetLocation = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const location = await getCurrentLocation();
      onLocationUpdate(location);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get location');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`rounded-xl shadow-lg ${isDark ? 'bg-gray-800' : 'bg-white'} p-4 sm:p-6 mb-6 sm:mb-8`}>
      <div className="flex items-center gap-3 mb-4">
        <MapPin className="w-5 h-5" style={getIconColor(theme)} />
        <h2 className="text-base sm:text-lg font-semibold">
          {getTranslation('location.title', language)}
        </h2>
      </div>

      <div className="space-y-4">
        {currentLocation && (
          <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
            <p className="text-sm font-medium mb-1">
              {getTranslation('location.current', language)}:
            </p>
            <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'} break-words`}>
              {currentLocation.fullAddress || `${currentLocation.city || 'Unknown City'}, ${currentLocation.country || 'Unknown Country'}`}
            </p>
            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
              ({currentLocation.latitude.toFixed(6)}, {currentLocation.longitude.toFixed(6)})
            </p>
          </div>
        )}

        <button
          onClick={handleGetLocation}
          disabled={isLoading}
          className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full sm:w-auto"
          style={getButtonStyles(theme)}
          onMouseEnter={(e) => {
            if (!isLoading) {
              e.currentTarget.style.backgroundColor = getButtonStyles(theme)[':hover'].backgroundColor;
            }
          }}
          onMouseLeave={(e) => {
            if (!isLoading) {
              e.currentTarget.style.backgroundColor = getButtonStyles(theme).backgroundColor;
            }
          }}
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Navigation className="w-4 h-4" />
          )}
          <span className="text-sm sm:text-base">
            {isLoading 
              ? getTranslation('location.getting', language)
              : getTranslation('location.update', language)
            }
          </span>
        </button>

        {error && (
          <p className="text-red-500 text-sm break-words">{error}</p>
        )}
      </div>
    </div>
  );
}